import { useState, useRef, ReactNode } from 'react'
import { SearchableCampaign } from 'types/campaigns'
import useOutsideAlert from 'hooks/useOutsideAlert'
import { getEnumValue } from 'helpers/enumHelper'
import {
  CampaignActionRequestCampaignAction,
  CampaignAdminModelStage,
} from '@urbanx/agx.campaigns.client'
import { Images } from '@urbanx/agx-ui-components'

interface CampaignOptionProps {
  onPerformCampaignAction(
    campaignId: string,
    campaignAction: CampaignActionRequestCampaignAction
  ): void
  selectedCampaign: SearchableCampaign
  isArchived: boolean
  dataTestId?: string
}

const CampaignOptions = (props: CampaignOptionProps) => {
  const { onPerformCampaignAction, selectedCampaign, isArchived, dataTestId } =
    props

  const [isOpen, setIsOpen] = useState(false)

  const moreOptionsRef = useRef(null)
  useOutsideAlert(moreOptionsRef, (onTriggerOutside: string) => {
    if (onTriggerOutside === 'outside') setIsOpen(false)
  })

  const handleToggleOpen = () => {
    setIsOpen(!isOpen)
  }

  const ActionLink = ({
    action,
    children,
  }: {
    action: CampaignActionRequestCampaignAction
    children: ReactNode
  }) => (
    <div>
      <a
        data-testid={`${dataTestId}-${action.toLowerCase()}`}
        onClick={() => {
          onPerformCampaignAction(selectedCampaign?.id || '', action)
          setIsOpen(false)
        }}
      >
        {children}
      </a>
    </div>
  )

  const actionsByStage = (campaignStage?: CampaignAdminModelStage) => {
    switch (getEnumValue(CampaignAdminModelStage, campaignStage)) {
      case CampaignAdminModelStage.AgreementDraft:
      case CampaignAdminModelStage.AgreementAgentSigning:
      case CampaignAdminModelStage.AgreementVendorSigning:
      case CampaignAdminModelStage.AgreementSignedByAllParties:
        return [
          <ActionLink
            key="archive"
            action={CampaignActionRequestCampaignAction.Archive}
          >
            Archive
          </ActionLink>,
        ]
      case CampaignAdminModelStage.ListingCurrent:
        return [
          <ActionLink
            key="markAsSold"
            action={CampaignActionRequestCampaignAction.MarkAsSold}
          >
            Mark as Sold
          </ActionLink>,
          <ActionLink
            key="markAsUnderContract"
            action={CampaignActionRequestCampaignAction.MarkAsUnderContract}
          >
            Mark as Under Contract
          </ActionLink>,
          <ActionLink
            key="withdraw"
            action={CampaignActionRequestCampaignAction.Withdraw}
          >
            Withdraw
          </ActionLink>,
        ]
      case CampaignAdminModelStage.ListingUnderContract:
        return [
          <ActionLink
            key="markAsSold"
            action={CampaignActionRequestCampaignAction.MarkAsSold}
          >
            Mark as Sold
          </ActionLink>,
          <ActionLink
            key="revertToCurrent"
            action={CampaignActionRequestCampaignAction.RevertToCurrent}
          >
            Revert to Current
          </ActionLink>,
        ]
      default:
        return []
    }
  }

  const actions = actionsByStage(selectedCampaign.stage)

  return (
    <div
      onClick={handleToggleOpen}
      className="hover-container"
      key={selectedCampaign.id}
      ref={moreOptionsRef}
      data-testid={`${dataTestId}`}
    >
      <Images.More />
      {isOpen && (
        <div className="hover-element" id={selectedCampaign.id}>
          {!isArchived && actions.length > 0 ? (
            actions
          ) : (
            <ActionLink
              key="default"
              action={
                isArchived
                  ? CampaignActionRequestCampaignAction.Restore
                  : CampaignActionRequestCampaignAction.Archive
              }
            >
              {isArchived
                ? CampaignActionRequestCampaignAction.Restore
                : CampaignActionRequestCampaignAction.Archive}
            </ActionLink>
          )}
        </div>
      )}
    </div>
  )
}

export default CampaignOptions
